import { OrderDraftSnapshot, filledSequences, isOrdered } from '../order-draft';
import {
  SalesOrderState,
  SalesOrderStatus,
} from '@omni/enums/order-capture/sales-order.enum';
import {
  formattedText,
  idOf,
  isoOf,
  numberOrNull,
  text,
} from '@omni/utility/dynamics-row.util';
import { AppUiRecordDoc } from '@omni/interfaces/app-ui/record.interface';
import { CURRENCY_CODE } from '../order-currency';
import { OverlayTone } from '@omni/interfaces/app-ui/overlay.interface';
import { localDayOf } from '@omni/utility/iso-day.util';

/**
 * One order as the order lists show it (the Order Management tool now, an account's History
 * later): from the rep's synced orders or from this device's own record of one.
 */
export interface OrderListRow {
  /** The `salesorder` id, minted on the device, so both copies of an order share it. */
  id: string;
  /** Empty until the server numbers the order. */
  orderNumber: string;
  accountId: string | null;
  accountName: string;
  state: SalesOrderState;
  status: { code: number | null; label: string; tone: OverlayTone };
  total: number | null;
  totalText: string;
  /** ISO code; null when the order does not say (a device order is in the rep's own). */
  currency: string | null;
  /** The deliveries holding quantities. */
  deliveries: number | null;
  /** `yyyy-MM-dd`. */
  firstDelivery: string | null;
  /** The first product ordered, by name; empty when not known. */
  firstProduct: string;
  /** The distinct products ordered, free goods aside. */
  productCount: number | null;
  createdOn: string;
  modifiedOn: string;
  /** Couldn't be saved or submitted: refused, or the send gave up. */
  attention: boolean;
  /** The rep created, or owns, it (or SaveOrder created it on their behalf); else another rep's. */
  mine: boolean;
  ownerName: string;
  /** Set when the row is this device's record, which is what opening it uses. */
  localId: string | null;
}

/** The configured status words (`badgeStyles`), by status code. */
export type OrderStatusStyles = Readonly<
  Record<string, { label: string; tone: OverlayTone }>
>;

/** Which orders a list holds: one account's, and only some statuses (History's configured ones). */
export interface OrderListScope {
  accountId?: string | null;
  statusCodes?: readonly number[] | null;
}

export type OrderListQuickFilter = 'attention' | 'thisWeek' | 'thisMonth';

export interface OrderListFilter {
  /** Null: every state. */
  states: readonly SalesOrderState[] | null;
  quick: ReadonlySet<OrderListQuickFilter>;
  /** Statuses picked from the list's own status chips; empty: all. */
  statusCodes: ReadonlySet<number>;
  search: string;
}

export type OrderListSortColumn =
  | 'order'
  | 'account'
  | 'status'
  | 'total'
  | 'products'
  | 'deliveries'
  | 'created'
  | 'changed';

export interface OrderListSort {
  column: OrderListSortColumn;
  descending: boolean;
}

/** The columns the rep's orders are read with, synced or fetched. */
export const ORDER_LIST_ATTRIBUTES = [
  'salesorderid',
  'ordernumber',
  'statecode',
  'statuscode',
  'totalamount',
  'createdon',
  'modifiedon',
  'customerid',
  'requestdeliveryby',
  'indskr_deliverycount',
  'ownerid',
  'createdby',
  'createdonbehalfby',
] as const;

const TONE_BY_STATE: Record<number, OverlayTone> = {
  [SalesOrderState.Active]: 'neutral',
  [SalesOrderState.Submitted]: 'info',
  [SalesOrderState.Fulfilled]: 'success',
  [SalesOrderState.Invoiced]: 'success',
};

/** What `summarizeOrderLines` adds to an order from its lines; absent until it has. */
export const LINE_SUMMARY = {
  firstProduct: 'lines.firstproduct',
  products: 'lines.products',
  deliveries: 'lines.deliveries',
  firstDelivery: 'lines.firstdelivery',
  /** The server refused to summarize it; asked again only once its lines change. */
  failed: 'lines.failed',
} as const;

/** Formats a total as the order screen does: Dynamics' own text can differ in precision. */
export type OrderListMoney = (
  amount: number,
  currency: string | null,
) => string;

/** A synced or fetched `salesorder` row; `userId` (the rep) tells their orders from another rep's. */
export function rowFromServer(
  raw: Record<string, unknown>,
  styles: OrderStatusStyles,
  money: OrderListMoney,
  userId: string | null,
): OrderListRow {
  const me = idOf(userId);
  const state = Number(raw.statecode) as SalesOrderState;
  const code = numberOrNull(raw.statuscode);
  const style = code === null ? undefined : styles[String(code)];
  const total = numberOrNull(raw.totalamount);
  const currency = text(raw[CURRENCY_CODE]) || null;
  return {
    id: idOf(raw.salesorderid),
    orderNumber: text(raw.ordernumber),
    accountId: idOf(raw._customerid_value) || null,
    accountName: formattedText(raw, '_customerid_value'),
    state,
    status: {
      code,
      label: style?.label ?? formattedText(raw, 'statuscode'),
      tone: style?.tone ?? TONE_BY_STATE[state] ?? 'neutral',
    },
    total,
    totalText: total === null ? '' : money(total, currency),
    currency,
    ...lineSummary(raw),
    createdOn: isoOf(raw.createdon),
    modifiedOn: isoOf(raw.modifiedon || raw.createdon),
    attention: false,
    mine:
      !me ||
      [
        raw._createdby_value,
        raw._createdonbehalfby_value,
        raw._ownerid_value,
      ].some((id) => idOf(id) === me),
    ownerName: formattedText(raw, '_ownerid_value'),
    localId: null,
  };
}

/** What a device row needs that its record does not say: the account's name, money, status words. */
export interface DeviceRowWords {
  accountName(accountId: string | null): string;
  money(amount: number, currency: string | null): string;
  statusLabel(record: AppUiRecordDoc): string;
  state(record: AppUiRecordDoc): SalesOrderState;
}

/** An order this device holds: captured here, or a draft pulled to carry on with. */
export function rowFromDevice(
  record: AppUiRecordDoc,
  words: DeviceRowWords,
): OrderListRow {
  const snapshot = record.state as Partial<OrderDraftSnapshot> | null;
  const state = words.state(record);
  const used = filledSequences(snapshot?.lines);
  const filled = (snapshot?.deliveries ?? []).filter((d) =>
    used.has(d.sequence),
  );
  const dates = filled
    .map((delivery) => delivery.date)
    .filter((date): date is string => !!date)
    .sort();
  const attention =
    record.syncStatus === 'refused' || record.syncStatus === 'unknown';
  const net = typeof snapshot?.net === 'number' ? snapshot.net : null;
  const accountId = record.parentId?.toLowerCase() ?? null;
  const currency = snapshot?.currency ?? null;
  return {
    id: deviceOrderId(record),
    orderNumber: record.serverName ?? '',
    accountId,
    accountName: words.accountName(accountId),
    state,
    status: deviceStatus(record, state, attention, words),
    total: net,
    totalText: net === null ? '' : words.money(net, currency),
    currency,
    deliveries: filled.length || null,
    firstDelivery: dates[0] ?? null,
    firstProduct: snapshot?.firstProduct ?? '',
    productCount: (snapshot?.lines ?? []).filter(isOrdered).length || null,
    createdOn: record.createdOn ?? '',
    modifiedOn: record.modifiedOn || record.createdOn || '',
    attention,
    mine: true,
    ownerName: '',
    localId: record.localId,
  };
}

function deviceStatus(
  record: AppUiRecordDoc,
  state: SalesOrderState,
  attention: boolean,
  words: DeviceRowWords,
): OrderListRow['status'] {
  return {
    code:
      state === SalesOrderState.Submitted
        ? SalesOrderStatus.Submitted
        : SalesOrderStatus.Draft,
    label: words.statusLabel(record),
    tone: attention ? 'danger' : (TONE_BY_STATE[state] ?? 'neutral'),
  };
}

/** The order a device record holds, by the id its synced row has. */
export function deviceOrderId(record: AppUiRecordDoc): string {
  const snapshot = record.state as Partial<OrderDraftSnapshot> | null;
  return idOf(record.serverId ?? snapshot?.orderId ?? record.localId);
}

/**
 * One row per order. The device's copy wins while it holds what the server lacks (unsent changes,
 * a refusal); otherwise the synced row is newer about where the order stands. A `stale` copy (one
 * the last sync no longer returned: out of the window, cancelled or moved elsewhere) is left out.
 */
export function mergeOrderRows(
  synced: readonly OrderListRow[],
  device: readonly { row: OrderListRow; ahead: boolean; stale?: boolean }[],
): OrderListRow[] {
  const byId = new Map(synced.map((row) => [row.id, row]));
  for (const { row, ahead, stale } of device) {
    const theirs = byId.get(row.id);
    if (!theirs) {
      if (ahead || !stale) byId.set(row.id, row);
    } else if (ahead) byId.set(row.id, withServerNames(row, theirs));
    else byId.set(row.id, { ...theirs, localId: row.localId });
  }
  return [...byId.values()];
}

/**
 * A device copy ahead of the server, with what only the server knows: the names it has given, and
 * when it created the order (a pulled draft's record dates from the pull).
 */
function withServerNames(
  row: OrderListRow,
  theirs: OrderListRow,
): OrderListRow {
  return {
    ...row,
    orderNumber: row.orderNumber || theirs.orderNumber,
    accountName: row.accountName || theirs.accountName,
    firstProduct: row.firstProduct || theirs.firstProduct,
    createdOn: theirs.createdOn || row.createdOn,
  };
}

export function inScope(row: OrderListRow, scope: OrderListScope): boolean {
  if (scope.accountId && row.accountId !== idOf(scope.accountId)) return false;
  return (
    !scope.statusCodes?.length ||
    (row.status.code !== null && scope.statusCodes.includes(row.status.code))
  );
}

/**
 * The rows a filter lets through. Built once per filter, so the search text and the week and month
 * starts are worked out once, not per row.
 */
export function orderFilter(
  filter: OrderListFilter,
  now = new Date(),
): (row: OrderListRow) => boolean {
  const search = filter.search.trim().toLowerCase();
  const weekStart = startOf('week', now);
  const monthStart = startOf('month', now);
  return (row) => {
    if (filter.states && !filter.states.includes(row.state)) return false;
    if (
      filter.statusCodes.size &&
      (row.status.code === null || !filter.statusCodes.has(row.status.code))
    )
      return false;
    if (filter.quick.has('attention') && !row.attention) return false;
    const created = Date.parse(row.createdOn);
    if (filter.quick.has('thisWeek') && !(created >= weekStart)) return false;
    if (filter.quick.has('thisMonth') && !(created >= monthStart)) return false;
    return !search || searchText(row).includes(search);
  };
}

/** What a search matches: the order's number, account, owner and first product. */
const SEARCH_TEXT = new WeakMap<OrderListRow, string>();

function searchText(row: OrderListRow): string {
  let found = SEARCH_TEXT.get(row);
  if (found === undefined) {
    found = [row.orderNumber, row.accountName, row.ownerName, row.firstProduct]
      .join('\n')
      .toLowerCase();
    SEARCH_TEXT.set(row, found);
  }
  return found;
}

/** Text in the rep's language order, case aside; numbers within it by value. */
const TEXT_ORDER = new Intl.Collator(undefined, {
  sensitivity: 'base',
  numeric: true,
});

/** Empty values sort last whichever way the column runs; ties go newest change first. */
export function sortOrderRows(
  rows: readonly OrderListRow[],
  sort: OrderListSort,
): OrderListRow[] {
  const key = SORT_KEYS[sort.column];
  return [...rows].sort((a, b) => {
    const [x, y] = [key(a), key(b)];
    if (x === y) return compareIso(b.modifiedOn, a.modifiedOn);
    if (x === null) return 1;
    if (y === null) return -1;
    const order =
      typeof x === 'number'
        ? x - (y as number)
        : TEXT_ORDER.compare(x, String(y));
    return sort.descending ? -order : order;
  });
}

function compareIso(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

const SORT_KEYS: Record<
  OrderListSortColumn,
  (row: OrderListRow) => string | number | null
> = {
  order: (row) => row.orderNumber || null,
  account: (row) => row.accountName || null,
  status: (row) => row.status.label || null,
  total: (row) => row.total,
  products: (row) => row.firstProduct || null,
  deliveries: (row) => row.firstDelivery,
  created: (row) => row.createdOn || null,
  changed: (row) => row.modifiedOn || null,
};

/** The start of this week (Monday) or month, on the rep's clock. */
function startOf(period: 'week' | 'month', now: Date): number {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (period === 'month') start.setDate(1);
  else start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start.getTime();
}

/**
 * What the sync read from the order's lines. Until it has (or when the server refused), the
 * schedule's count and the order's first delivery stand in.
 */
function lineSummary(
  raw: Record<string, unknown>,
): Pick<
  OrderListRow,
  'deliveries' | 'firstDelivery' | 'firstProduct' | 'productCount'
> {
  const summarized = LINE_SUMMARY.products in raw;
  const firstDelivery = text(raw[LINE_SUMMARY.firstDelivery]);
  return {
    deliveries: summarized
      ? numberOrNull(raw[LINE_SUMMARY.deliveries]) || null
      : numberOrNull(raw.indskr_deliverycount),
    firstDelivery: firstDelivery || localDayOf(isoOf(raw.requestdeliveryby)),
    firstProduct: text(raw[LINE_SUMMARY.firstProduct]),
    productCount: numberOrNull(raw[LINE_SUMMARY.products]) || null,
  };
}

/** A month's heading in a list sorted by a date; `id` is its row key, so the list can jump to it. */
export interface OrderListMonth {
  kind: 'month';
  id: string;
  /** `yyyy-MM`, on the rep's clock. */
  month: string;
  count: number;
  /** Null when its orders are in more than one currency. */
  total: { amount: number; currency: string | null } | null;
}

export type OrderListItem = OrderListRow | OrderListMonth;

export function isMonth(item: OrderListItem): item is OrderListMonth {
  return (item as OrderListMonth).kind === 'month';
}

/**
 * Rows in their order with a heading before each month of `dateOf`. The rows must already be
 * sorted by that date, so each month is one run; a month with no orders gets no heading.
 */
export function groupByMonth(
  rows: readonly OrderListRow[],
  dateOf: (row: OrderListRow) => string,
): OrderListItem[] {
  const items: OrderListItem[] = [];
  let heading: OrderListMonth | null = null;
  let currencies = new Set<string>();
  for (const row of rows) {
    const month = monthOf(dateOf(row));
    if (!heading || heading.month !== month) {
      heading = {
        kind: 'month',
        id: `month:${month}`,
        month,
        count: 0,
        total: { amount: 0, currency: null },
      };
      currencies = new Set();
      items.push(heading);
    }
    heading.count++;
    if (row.currency) currencies.add(row.currency);
    if (heading.total && currencies.size > 1) heading.total = null;
    if (heading.total) {
      heading.total = {
        amount: heading.total.amount + (row.total ?? 0),
        currency: row.currency ?? heading.total.currency,
      };
    }
    items.push(row);
  }
  return items;
}

/** `yyyy-MM` on the rep's clock; empty for a date that cannot be read. */
function monthOf(iso: string): string {
  const time = Date.parse(iso);
  if (Number.isNaN(time)) return '';
  const date = new Date(time);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
